import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  Inventory2 as Inventory2Icon,
  LocationOn as LocationOnIcon,
  Comment as CommentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP, fpInputSx } from 'components/financepro';

const TABLA_INVENTARIO = 'productosnoserializados';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : String(v);
};

const CampoInfo = ({ label, value, fullWidth = false }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 3}>
    <Box
      sx={{
        height: '100%',
        p: 1.5,
        borderRadius: 1.6,
        bgcolor: FP.color.surfaceAlt,
        border: `1px solid ${FP.color.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: FP.color.textMuted,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.95rem',
          fontWeight: 700,
          color: FP.color.textPrimary,
          wordBreak: 'break-word',
          lineHeight: 1.35,
        }}
      >
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Box>
  </Grid>
);

/**
 * Diálogo reutilizable para consultar productos no serializados.
 * Busca vía `/api/productosbackend_buscarproductos` y carga el detalle
 * con `/api/productosbackend_obtenerproducto`. Con un click se pueden
 * expandir localización y comentarios (observaciones / garantía).
 */
const ConsultaProducto = ({ open, onClose }) => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const localidadActual = useMemo(
    () =>
      localStorage.getItem('sucursal') ||
      localStorage.getItem('localidad') ||
      `${selectedCompany?.almacenpordefecto ?? '0'}-`,
    [selectedCompany]
  );

  const [busqueda, setBusqueda] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [productoSel, setProductoSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [verLocalizacion, setVerLocalizacion] = useState(false);
  const [verComentarios, setVerComentarios] = useState(false);

  const resetEstado = useCallback(() => {
    setBusqueda('');
    setOpciones([]);
    setProductoSel(null);
    setDetalle(null);
    setCargandoDetalle(false);
    setVerLocalizacion(false);
    setVerComentarios(false);
  }, []);

  useEffect(() => {
    if (!open) resetEstado();
  }, [open, resetEstado]);

  const buscarProductos = useCallback(
    async (texto) => {
      if (!selectedCompany?.db_empresa || !usuarioLog) {
        setOpciones([]);
        return;
      }
      setBuscando(true);
      try {
        const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tabla_inventario: TABLA_INVENTARIO,
            descripcion: texto || '',
            local: localidadActual,
          }),
        });
        if (!response.ok) {
          setOpciones([]);
          return;
        }
        const data = await response.json();
        setOpciones(data.productos || []);
      } catch {
        setOpciones([]);
      } finally {
        setBuscando(false);
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, localidadActual]
  );

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      buscarProductos(busqueda.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda, open, buscarProductos]);

  const cargarDetalle = async (codigo) => {
    if (!codigo || !selectedCompany?.db_empresa) return;
    setCargandoDetalle(true);
    setVerLocalizacion(false);
    setVerComentarios(false);
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: TABLA_INVENTARIO,
          codigo,
          local: localidadActual,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al obtener el producto');
        setDetalle(null);
        return;
      }
      const data = await response.json();
      setDetalle(data.producto || null);
    } catch {
      showError('Error de conexión al obtener el producto');
      setDetalle(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const handleSeleccionar = (_, value) => {
    setProductoSel(value);
    if (value?.codigo) {
      cargarDetalle(value.codigo);
    } else {
      setDetalle(null);
    }
  };

  const handleCerrar = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCerrar}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: '96vw', sm: '92vw', md: 1100 },
          maxWidth: 1100,
          height: { xs: '92vh', md: '88vh' },
          maxHeight: { xs: '92vh', md: '88vh' },
          m: { xs: 1, sm: 2 },
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          bgcolor: '#FAFBFE',
          borderBottom: `1px solid ${FP.color.border}`,
          py: 2,
          px: { xs: 2, sm: 3 },
          pr: { xs: 1.5, sm: 2 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.4}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.4,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Inventory2Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.2rem' }}>
              Consulta de producto
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
              Información completa · localización y comentarios
            </Typography>
          </Box>
          <IconButton onClick={handleCerrar} size="small" sx={{ color: FP.color.textMuted }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Búsqueda fija fuera del área con scroll para no quedar bajo el título */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          pt: 2.5,
          pb: 2,
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
        }}
      >
        <Autocomplete
          options={opciones}
          getOptionLabel={(o) => o.texto || `${o.codigo || ''} - ${o.descripcion || ''}`}
          isOptionEqualToValue={(a, b) => String(a?.codigo) === String(b?.codigo)}
          value={productoSel}
          onChange={handleSeleccionar}
          inputValue={busqueda}
          onInputChange={(_, v) => setBusqueda(v)}
          loading={buscando}
          filterOptions={(x) => x}
          noOptionsText={buscando ? 'Buscando…' : 'Sin productos'}
          ListboxProps={{ style: { maxHeight: 280 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar producto"
              placeholder="Escriba código o descripción…"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: FP.color.textMuted, mr: 0.5, ml: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {buscando ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                ...fpInputSx,
                '& .MuiOutlinedInput-root': {
                  ...fpInputSx['& .MuiOutlinedInput-root'],
                  height: 52,
                  minHeight: 52,
                },
              }}
            />
          )}
        />
      </Box>

      <DialogContent
        dividers={false}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2.5,
          bgcolor: FP.color.surface,
        }}
      >
        {cargandoDetalle && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress size={36} sx={{ color: FP.color.navy }} />
          </Box>
        )}

        {!cargandoDetalle && !detalle && (
          <Box
            sx={{
              py: 8,
              px: 2,
              textAlign: 'center',
              borderRadius: 2.5,
              border: `1px dashed ${FP.color.borderStrong}`,
              bgcolor: FP.color.cardBg,
              minHeight: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 1.5 }} />
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.1rem' }}>
              Seleccione un producto
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', mt: 0.75, maxWidth: 420 }}>
              Use el buscador de arriba y elija un ítem para ver toda su información, localización y
              comentarios.
            </Typography>
          </Box>
        )}

        {!cargandoDetalle && detalle && (
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2.5,
              bgcolor: FP.color.cardBg,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.soft,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mb: 2.5 }}
            >
              {detalle.foto_base64 && (
                <Box
                  component="img"
                  src={`data:image/jpeg;base64,${detalle.foto_base64}`}
                  alt={detalle.descripcion || 'Producto'}
                  sx={{
                    width: 112,
                    height: 112,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                  <Chip
                    label={detalle.codigo}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      bgcolor: FP.color.navy,
                      color: '#fff',
                      height: 28,
                    }}
                  />
                  {detalle.tipoproducto ? (
                    <Chip
                      label={detalle.tipoproducto}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                      }}
                    />
                  ) : null}
                </Stack>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: FP.color.textPrimary,
                    fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    lineHeight: 1.3,
                  }}
                >
                  {detalle.descripcion || 'Sin descripción'}
                </Typography>
                <Typography sx={{ mt: 0.75, color: FP.color.navy, fontWeight: 800, fontSize: '1.25rem' }}>
                  ${fmtMoney(detalle.precio)}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <CampoInfo label="Tipo" value={detalle.tipoproducto} />
              <CampoInfo label="Categoría" value={detalle.catproducto} />
              <CampoInfo label="Departamento" value={detalle.departamento} />
              <CampoInfo label="Medida" value={detalle.medida} />
              <CampoInfo label="Impuesto" value={detalle.nombreimpuesto || detalle.itbis} />
              <CampoInfo label="ITBIS %" value={fmtNum(detalle.itbis)} />
              <CampoInfo label="Costo" value={`$${fmtMoney(detalle.costo)}`} />
              <CampoInfo label="Mayor" value={`$${fmtMoney(detalle.mayor)}`} />
              <CampoInfo label="Precio" value={`$${fmtMoney(detalle.precio)}`} />
              <CampoInfo label="Existencia inicial" value={fmtNum(detalle.inicial)} />
              <CampoInfo label="Cant. caja" value={fmtNum(detalle.cantcaja)} />
              <CampoInfo label="Mín / Máx" value={`${fmtNum(detalle.cantmin)} / ${fmtNum(detalle.cantmax)}`} />
              <CampoInfo label="Cuenta inventario" value={detalle.cuentainvent} />
              <CampoInfo label="Cuenta gasto" value={detalle.cuentagasto} />
            </Grid>

            <Divider sx={{ my: 2.5, borderColor: FP.color.border }} />

            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
                mb: 1.25,
              }}
            >
              Más información
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.75 }}>
              <Button
                variant={verLocalizacion ? 'contained' : 'outlined'}
                startIcon={<LocationOnIcon />}
                onClick={() => setVerLocalizacion((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 1.4,
                  px: 2,
                  py: 1,
                  borderColor: FP.color.border,
                  ...(verLocalizacion
                    ? {
                        bgcolor: FP.color.navy,
                        color: '#fff',
                        '&:hover': { bgcolor: FP.color.navyDeep },
                      }
                    : {
                        color: FP.color.textPrimary,
                        bgcolor: '#FFFFFF',
                        '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                      }),
                }}
              >
                Localización
              </Button>
              <Button
                variant={verComentarios ? 'contained' : 'outlined'}
                startIcon={<CommentIcon />}
                onClick={() => setVerComentarios((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 1.4,
                  px: 2,
                  py: 1,
                  borderColor: FP.color.border,
                  ...(verComentarios
                    ? {
                        bgcolor: FP.color.navy,
                        color: '#fff',
                        '&:hover': { bgcolor: FP.color.navyDeep },
                      }
                    : {
                        color: FP.color.textPrimary,
                        bgcolor: '#FFFFFF',
                        '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                      }),
                }}
              >
                Comentarios
              </Button>
            </Stack>

            <Collapse in={verLocalizacion}>
              <Box
                sx={{
                  p: 2.25,
                  mb: 1.5,
                  borderRadius: 2,
                  bgcolor: FP.color.infoSoft,
                  border: `1px solid ${FP.color.border}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <LocationOnIcon sx={{ fontSize: 20, color: FP.color.info }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: FP.color.textPrimary }}>
                    Localización
                  </Typography>
                </Stack>
                <Grid container spacing={1.5}>
                  <CampoInfo label="Localidad / almacén" value={detalle.localidad} fullWidth />
                  <CampoInfo
                    label="Ubicación en inventario"
                    value={detalle.ubicacioninventario || 'Sin ubicación registrada'}
                    fullWidth
                  />
                </Grid>
              </Box>
            </Collapse>

            <Collapse in={verComentarios}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: 2,
                  bgcolor: FP.color.warningSoft,
                  border: `1px solid ${FP.color.border}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                  <CommentIcon sx={{ fontSize: 20, color: FP.color.warning }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: FP.color.textPrimary }}>
                    Comentarios / observaciones
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.95rem', color: FP.color.textPrimary, whiteSpace: 'pre-wrap' }}>
                  {(detalle.garantia || '').trim() || 'Sin comentarios registrados'}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
        }}
      >
        <Button
          onClick={handleCerrar}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            px: 2.5,
            bgcolor: FP.color.navy,
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsultaProducto;
